import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import InsLoader from "../../../../components/InsLoader/InsLoader";
import InsButton from "../../../../components/InsButton/InsButton";
import InsCheckbox from "../../../../components/InsCheckbox/InsCheckbox";
import StepCount from "../../../../components/StepCount/StepCount";
import { updateInfos } from "store/actions/createAccountAction";
import "./AxesStep.scss";

export default function AxesStep(props) {
  const { currentStep, maxSteps, onValidate, userInfo, axes, axesLoading } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);

  const [selectedIds, setSelectedIds] = useState(() => new Set(userInfo?.axes || []));

  const isAxesRequired = useMemo(() => {
    const axisConfig = config?.register?.preferenceAxis?.find((item) => item.name === "axis");
    return axisConfig?.required === true;
  }, [config]);

  const isNextDisabled = useMemo(() => {
    return axes?.length > 0 && isAxesRequired && selectedIds.size === 0;
  }, [isAxesRequired, selectedIds, axes]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const updatedIds = new Set(prev);
      if (updatedIds.has(id)) {
        updatedIds.delete(id);
      } else {
        updatedIds.add(id);
      }
      dispatch(updateInfos({ axes: Array.from(updatedIds) }));
      return updatedIds;
    });
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(axes) || axes.length === 0) {
      selectedIds.size > 0 && setSelectedIds(new Set());
      return;
    }

    const available = new Set(axes.map((a) => a.axis_id));

    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (!available.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      const updatedIds = changed ? next : prev;
      dispatch(updateInfos({ axes: Array.from(updatedIds) }));
      return updatedIds;
    });
  }, [axes]);

  const sortedAxes = useMemo(() => {
    if (!Array.isArray(axes)) return [];
    return [...axes].sort((a, b) => (a.axis_name || "").localeCompare(b.axis_name || ""));
  }, [axes]);

  return (
    <form className="default-container col-flex" onSubmit={(e) => e.preventDefault()}>
      <StepCount step={currentStep} maxStepCount={maxSteps} />
      <div className="axesHeader">
        <p className="bold-label centered">{t("axisStep_title")}</p>
        <p className="axesSubTitle">{t("axisStep_subtitle")}</p>
      </div>

      <section className="axesSection" aria-label="Select axes">
        <div className="axesScroll">
          {axesLoading ? (
            <div className="axesLoading">
              <InsLoader />
            </div>
          ) : sortedAxes.length > 0 ? (
            <div className="axisList">
              {sortedAxes.map((axis) => {
                  const id = axis.axis_id;
                  return (
                    <InsCheckbox
                      key={id}
                      name={`axis-${id}`}
                      label={axis.axis_name}
                      checked={selectedIds.has(id)}
                      onClick={() => toggle(id)}
                      className="axisCheckbox"
                    />
                  );
                })}
            </div>
          ) : (
            <div className="noAxesSection">
              <p className="noAxesLabel">{t("no_axes_for_retailer")}</p>
            </div>
          )}
        </div>
      </section>

      <div className="bottom-container">
        {isNextDisabled && <div className="alert_msg mb10">{t("select_atleast_one_axis")}</div>}
        <InsButton
          text={t("next")}
          onClick={() => onValidate({ axes: Array.from(selectedIds) })}
          disabled={isNextDisabled}
        />
      </div>
    </form>
  );
}
